(() => {
  const explicitSelector = '[data-feesbees-reveal]';
  const autoSelector = [
    '#MainContent img',
    '#MainContent h1',
    '#MainContent h2',
    '#MainContent h3',
    '#MainContent h4',
    '#MainContent h5',
    '#MainContent h6',
    '#MainContent p'
  ].join(',');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const observed = new WeakSet();

  const isSkipped = (element) => Boolean(element.closest('[data-feesbees-skip-reveal]'));

  const hasRevealAncestor = (element) => {
    const owner = element.parentElement?.closest(explicitSelector);
    return Boolean(owner && owner !== element);
  };

  const reveal = (element) => {
    element.classList.add('is-feesbees-visible');
  };

  const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return rect.bottom > 0 && rect.top < viewportHeight * 0.94;
  };

  const autoAnnotate = (scope = document) => {
    const candidates = [];

    if (scope.matches?.(autoSelector)) candidates.push(scope);
    scope.querySelectorAll?.(autoSelector).forEach((element) => candidates.push(element));

    candidates.forEach((element, index) => {
      if (isSkipped(element) || hasRevealAncestor(element) || element.matches(explicitSelector)) return;

      if (element.tagName === 'IMG') {
        element.dataset.feesbeesReveal = 'zoom';
      } else {
        element.dataset.feesbeesReveal = 'up';
      }

      element.style.setProperty('--feesbees-reveal-delay', `${Math.min(index % 4, 3) * 55}ms`);
    });
  };

  let observer = null;

  if (!reduceMotion && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
        observer?.unobserve(entry.target);
      });
    }, {
      threshold: 0.08,
      rootMargin: '0px 0px -4% 0px'
    });
  }

  const registerElement = (element) => {
    if (observed.has(element) || isSkipped(element)) return;
    observed.add(element);

    if (reduceMotion || !observer || isInViewport(element)) {
      reveal(element);
      return;
    }

    observer.observe(element);
  };

  const register = (scope = document) => {
    autoAnnotate(scope);

    const elements = [];
    if (scope.matches?.(explicitSelector)) elements.push(scope);
    scope.querySelectorAll?.(explicitSelector).forEach((element) => elements.push(element));

    elements.forEach(registerElement);
  };

  const boot = () => {
    register(document);

    // Only enable the hidden starting state after elements have been registered.
    // This prevents first-paint flashes and guarantees above-the-fold content stays visible.
    requestAnimationFrame(() => {
      document.documentElement.classList.add('feesbees-reveal-ready');
    });

    // Shopify can replace section markup without a full page refresh.
    document.addEventListener('shopify:section:load', (event) => register(event.target));
    document.addEventListener('shopify:block:select', (event) => {
      register(event.target);
      event.target?.querySelectorAll?.(explicitSelector).forEach(reveal);
      if (event.target?.matches?.(explicitSelector)) reveal(event.target);
    });

    // Also catch product/section content inserted asynchronously by the theme.
    const main = document.querySelector('#MainContent');
    if (main && 'MutationObserver' in window) {
      const mutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) register(node);
          });
        });
      });
      mutationObserver.observe(main, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
